import React, { useCallback, useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import axios from "axios";

import {
  activatePlatformIdentityProvider,
  createPlatformIdentityProvider,
  disablePlatformIdentityProvider,
  listPlatformIdentityProviders,
  type CreatePlatformIdentityProviderInput,
  type PlatformIdentityProvider,
  validatePlatformIdentityProvider,
} from "src/domains/organization/api/platformIdentityApi";
import { sectionLabelSx, getCardStyles } from "src/shell/styles/themeUtils";

interface PlatformIdentityProviderCardProps {
  orgAlias: string;
}

interface ProviderFormState extends CreatePlatformIdentityProviderInput {}

const initialForm: ProviderFormState = {
  alias: "",
  displayName: "",
  issuer: "",
  metadataUrl: "",
  authorizationEndpoint: "",
  tokenEndpoint: "",
  jwksUri: "",
  clientId: "",
  clientSecretOpenBaoPath: "",
  verifiedDomains: [],
};

function isPermissionError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 403;
}

function readableError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail =
      error.response?.data?.detail ?? error.response?.data?.message;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.join(", ");
  }
  return error instanceof Error ? error.message : fallback;
}

function statusLabel(provider: PlatformIdentityProvider): string {
  if (provider.status === "disabled") return "Disabled";
  if (
    provider.status === "active" &&
    provider.reconciliationStatus === "ready" &&
    provider.reconciledGeneration === provider.generation
  ) {
    return "Active";
  }
  if (
    provider.validationStatus === "failed" ||
    provider.status === "error" ||
    provider.reconciliationStatus === "failed"
  ) {
    return "Needs attention";
  }
  if (provider.validationStatus === "validated") return "Validated";
  return "Needs validation";
}

function statusColor(
  provider: PlatformIdentityProvider,
): "success" | "error" | "info" | "warning" {
  const label = statusLabel(provider);
  if (label === "Active") return "success";
  if (label === "Needs attention") return "error";
  if (label === "Validated") return "info";
  return "warning";
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function providerFormError(form: ProviderFormState): string | null {
  if (
    !form.alias.trim() ||
    !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(form.alias)
  ) {
    return "Use a lowercase provider alias with letters, numbers, and hyphens.";
  }
  if (!form.displayName.trim() || !form.clientId.trim()) {
    return "Display name and client ID are required.";
  }
  if (!form.clientSecretOpenBaoPath.trim()) {
    return "An OpenBao client-secret path is required for reconciliation.";
  }
  const urls = [
    form.issuer,
    form.metadataUrl,
    form.authorizationEndpoint,
    form.tokenEndpoint,
    form.jwksUri,
  ];
  if (urls.some((url) => !isHttpsUrl(url))) {
    return "All OIDC endpoints must use HTTPS.";
  }
  return null;
}

const PlatformIdentityProviderCard: React.FC<
  PlatformIdentityProviderCardProps
> = ({ orgAlias }) => {
  const theme = useTheme();
  const [providers, setProviders] = useState<PlatformIdentityProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ProviderFormState>(initialForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [disableTarget, setDisableTarget] =
    useState<PlatformIdentityProvider | null>(null);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setPermissionDenied(false);
    try {
      setProviders(await listPlatformIdentityProviders(orgAlias));
    } catch (error) {
      if (isPermissionError(error)) {
        setPermissionDenied(true);
      } else {
        setLoadError(
          readableError(error, "Unable to load identity providers."),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  const formValidationError = useMemo(() => providerFormError(form), [form]);

  const updateForm = <K extends keyof ProviderFormState>(
    key: K,
    value: ProviderFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFormError(null);
  };

  const openForm = () => {
    setForm({ ...initialForm });
    setFormError(null);
    setFormOpen(true);
  };

  const handleCreate = async () => {
    if (formValidationError) {
      setFormError(formValidationError);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const created = await createPlatformIdentityProvider(orgAlias, {
        ...form,
        alias: form.alias.trim(),
        displayName: form.displayName.trim(),
        clientId: form.clientId.trim(),
        clientSecretOpenBaoPath: form.clientSecretOpenBaoPath.trim(),
        verifiedDomains: form.verifiedDomains
          .map((domain) => domain.trim().toLowerCase())
          .filter(Boolean),
      });
      setProviders((current) => [...current, created]);
      setFormOpen(false);
    } catch (error) {
      setFormError(readableError(error, "Unable to register this provider."));
    } finally {
      setSaving(false);
    }
  };

  const runProviderAction = async (
    provider: PlatformIdentityProvider,
    action: "validate" | "activate",
  ) => {
    setActionId(provider.id);
    setActionError(null);
    try {
      const updated =
        action === "validate"
          ? await validatePlatformIdentityProvider(orgAlias, provider.id)
          : await activatePlatformIdentityProvider(orgAlias, provider.id);
      setProviders((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (error) {
      setActionError(
        readableError(error, `Unable to ${action} this provider.`),
      );
      await loadProviders();
    } finally {
      setActionId(null);
    }
  };

  const confirmDisable = async () => {
    if (!disableTarget) return;
    setActionId(disableTarget.id);
    setActionError(null);
    try {
      const updated = await disablePlatformIdentityProvider(
        orgAlias,
        disableTarget.id,
      );
      setProviders((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      setDisableTarget(null);
    } catch (error) {
      setActionError(readableError(error, "Unable to disable this provider."));
    } finally {
      setActionId(null);
    }
  };

  return (
    <>
      <Card sx={getCardStyles(theme)}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <SecurityIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={sectionLabelSx}>
                Platform identity federation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Let organization members sign in with your company identity
                provider while Rusha keeps ownership and permissions.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openForm}
              disabled={permissionDenied}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Add provider
            </Button>
          </Stack>

          <Alert
            severity="info"
            icon={<SecurityIcon fontSize="small" />}
            sx={{ borderLeft: `3px solid ${theme.palette.info.main}` }}
          >
            Client secrets stay in OpenBao. Provide the existing OpenBao path
            below; the browser never receives the secret.
          </Alert>

          {actionError && <Alert severity="error">{actionError}</Alert>}

          {loading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={78} />
              <Skeleton variant="rounded" height={78} />
            </Stack>
          ) : permissionDenied ? (
            <Alert severity="warning">
              Only organization admins can view or manage platform identity
              providers.
            </Alert>
          ) : loadError ? (
            <Alert
              severity="error"
              action={
                <Tooltip title="Retry">
                  <IconButton
                    color="inherit"
                    size="small"
                    onClick={() => void loadProviders()}
                    aria-label="Retry loading identity providers"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              {loadError}
            </Alert>
          ) : providers.length === 0 ? (
            <Box
              sx={{
                p: 2.5,
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                No provider configured
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Add your company OIDC provider, validate its metadata, then
                activate federation for this organization.
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={0}>
              {providers.map((provider) => {
                const busy = actionId === provider.id;
                const active = statusLabel(provider) === "Active";
                return (
                  <Stack
                    key={provider.id}
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    sx={{ py: 1.5 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          {provider.displayName}
                        </Typography>
                        <Chip
                          label={statusLabel(provider)}
                          color={statusColor(provider)}
                          size="small"
                          variant={active ? "filled" : "outlined"}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {provider.alias} · {provider.issuer}
                      </Typography>
                      {provider.validationError && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {provider.validationError}
                        </Typography>
                      )}
                      {provider.reconciliationError && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          Reconciliation: {provider.reconciliationError}
                        </Typography>
                      )}
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          busy ? (
                            <CircularProgress size={14} />
                          ) : (
                            <CheckCircleOutlineIcon />
                          )
                        }
                        disabled={busy || active}
                        onClick={() =>
                          void runProviderAction(provider, "validate")
                        }
                        sx={{ textTransform: "none" }}
                      >
                        Validate
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PlayArrowIcon />}
                        disabled={
                          busy ||
                          active ||
                          provider.validationStatus !== "validated"
                        }
                        onClick={() =>
                          void runProviderAction(provider, "activate")
                        }
                        sx={{ textTransform: "none" }}
                      >
                        Activate
                      </Button>
                      <Tooltip
                        title={
                          active ? "Disable federation" : "Remove provider"
                        }
                      >
                        <IconButton
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => setDisableTarget(provider)}
                          aria-label={`Disable ${provider.displayName}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Card>

      <Dialog
        open={formOpen}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add platform identity provider</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="info" icon={<WarningAmberIcon />}>
              Use exact values from your provider&apos;s OIDC discovery
              document. The server will re-check metadata and public endpoints
              before activation.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Provider alias"
                  value={form.alias}
                  onChange={(event) => updateForm("alias", event.target.value)}
                  helperText="Lowercase letters, numbers, and hyphens"
                  required
                  fullWidth
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Display name"
                  value={form.displayName}
                  onChange={(event) =>
                    updateForm("displayName", event.target.value)
                  }
                  required
                  fullWidth
                  disabled={saving}
                />
              </Grid>
              {(
                [
                  ["issuer", "Issuer URL"],
                  ["metadataUrl", "OIDC discovery URL"],
                  ["authorizationEndpoint", "Authorization endpoint"],
                  ["tokenEndpoint", "Token endpoint"],
                  ["jwksUri", "JWKS URL"],
                ] as const
              ).map(([key, label]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    label={label}
                    value={form[key]}
                    onChange={(event) => updateForm(key, event.target.value)}
                    required
                    fullWidth
                    disabled={saving}
                    placeholder="https://idp.example.com/..."
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="OIDC client ID"
                  value={form.clientId}
                  onChange={(event) =>
                    updateForm("clientId", event.target.value)
                  }
                  required
                  fullWidth
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="OpenBao client-secret path"
                  value={form.clientSecretOpenBaoPath}
                  onChange={(event) =>
                    updateForm("clientSecretOpenBaoPath", event.target.value)
                  }
                  required
                  fullWidth
                  disabled={saving}
                  helperText="Reference only; never paste the secret here."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Verified email domains"
                  value={form.verifiedDomains.join(", ")}
                  onChange={(event) =>
                    updateForm(
                      "verifiedDomains",
                      event.target.value
                        .split(",")
                        .map((domain) => domain.trim())
                        .filter(Boolean),
                    )
                  }
                  fullWidth
                  disabled={saving}
                  helperText="Comma-separated domains, for example acme.com"
                />
              </Grid>
            </Grid>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFormOpen(false)}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreate()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Registering…" : "Register provider"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={disableTarget !== null}
        onClose={() => actionId === null && setDisableTarget(null)}
      >
        <DialogTitle>Disable federation?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {disableTarget?.displayName} will stop being available for new
            organization sign-ins. Existing Rusha memberships are preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisableTarget(null)}
            disabled={actionId !== null}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmDisable()}
            disabled={actionId !== null}
            sx={{ textTransform: "none" }}
          >
            Disable provider
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlatformIdentityProviderCard;
